export type Filter = {
  type:
    | 'string'
    | 'single-select'
    | 'single_select'
    | 'multi-select'
    | 'multi_select'
    | 'date_range'
    | 'date-range';
  column: string;
  valuesLabels?: string[];
  start_date?: string;
  end_date?: string;
};

export const getValue = <T extends Record<string, unknown>>(
  obj: T,
  path: string,
): string =>
  (path
    .split('.')
    .reduce(
      (acc: unknown, key: string) => (acc as Record<string, unknown>)?.[key],
      obj,
    ) as string) ?? '';

import { Injector, runInInjectionContext } from '@angular/core';
import { ERMDateTimePipe } from '@shared/pipes/erm-date.pipe';
import { format } from 'date-fns'; // date-fns helps with date parsing/formatting

export const applySearch = <T extends Record<string, unknown>>(
  data: T[],
  columns: string[],
  query: string,
): T[] => {
  if (!query) return data;
  const search = query.trim().toLowerCase();

  return data.filter((doc) =>
    columns.some((col) => {
      const value = getValue(doc, col);
      if (value == null) return false;

      // Handle date search for specific columns
      if (['complete_timestamp', 'created_dt'].includes(col)) {
        try {
          const dateValue = new Date(value as string | number);
          const formatted = format(dateValue, 'dd-MMM-yyyy').toLowerCase();
          return formatted.includes(search);
        } catch {
          return false;
        }
      }
      // Handle boolean search for QC Status
      if (['is_complete'].includes(col)) {
        const searchVal = search.replaceAll(' ', '');
        const checkedVal = value ? 'checked' : 'notchecked';
        return checkedVal === searchVal;
      }

      // Default string search
      return value.toString().toLowerCase().includes(search);
    }),
  );
};

export const applyAdvancedFilters = <T extends Record<string, unknown>>(
  data: T[],
  filters: Filter[],
  injector: Injector,
): T[] => {
  return runInInjectionContext(injector, () => {
    const ermDateTimePipe = injector.get(ERMDateTimePipe);
    return data.filter((doc) =>
      filters.every((filter) => {
        const docValue = doc[filter.column];

        switch (filter.type) {
          case 'string':
            return filter.valuesLabels?.some((val) =>
              String(docValue ?? '')
                .toLowerCase()
                .includes(val.toLowerCase()),
            );

          case 'single-select':
          case 'single_select':
          case 'multi-select':
          case 'multi_select': {
            return filter.valuesLabels?.some((val) => {
              let valueStr: string;

              if (typeof docValue === 'object' && docValue !== null) {
                const obj = docValue as {
                  name?: string;
                  title?: string;
                  label?: string;
                };
                valueStr = obj.name ?? obj.title ?? obj.label ?? '';
              } else {
                valueStr = String(docValue ?? '');
              }
              if (valueStr === 'true' || valueStr === 'false') {
                valueStr = valueStr === 'true' ? 'Checked' : 'Not checked';
                return valueStr.toLowerCase() === val.toLowerCase();
              }

              return valueStr.toLowerCase().includes(val.toLowerCase());
            });
          }

          case 'date_range':
          case 'date-range': {
            if (!docValue) return false;

            // Helper to strip time portion
            const toDateOnly = (date: string | Date) => {
              const d = new Date(date);
              d.setHours(0, 0, 0, 0);
              return d.getTime();
            };

            const _docValue = docValue as string;
            const docDate = toDateOnly(
              ermDateTimePipe.transform(_docValue ?? ''),
            );
            const start = filter.start_date ? toDateOnly(filter.start_date) : 0;
            const end = filter.end_date ? toDateOnly(filter.end_date) : 0;

            return docDate >= start && docDate <= end;
          }

          default:
            return true;
        }
      }),
    );
  });
};

export const applySort = <T extends Record<string, any>>(
  data: T[],
  sortBy?: keyof T,
  order?: 'asc' | 'desc' | string,
): T[] => {
  if (!sortBy || !order) return data;

  const ascending = order.toLowerCase() === 'asc';

  return [...data].sort((a, b) => {
    let fieldA = a[sortBy];
    let fieldB = b[sortBy];

    // If the field is an object, try to sort by its `name` property
    if (typeof fieldA === 'object' && fieldA !== null) {
      fieldA = fieldA.name ?? '';
    }
    if (typeof fieldB === 'object' && fieldB !== null) {
      fieldB = fieldB.name ?? '';
    }

    // Convert everything to string for localeCompare sorting
    const valA = String(fieldA ?? '');
    const valB = String(fieldB ?? '');
    if (sortBy === 'is_complete')
      return valA.localeCompare(valB) * (ascending ? -1 : 1);
    return (
      valA.localeCompare(valB, undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * (ascending ? 1 : -1)
    );
  });
};

export const applyPagination = <T>(
  data: T[],
  page?: number,
  pageSize?: number,
): T[] => {
  const _page = page ?? 1;
  const _pageSize = pageSize ?? 10;
  const startIndex = (_page - 1) * _pageSize;

  return data.slice(startIndex, startIndex + _pageSize);
};
